import { getSession, signIn } from 'next-auth/react'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

// Track if we're currently in a sign-in flow to prevent infinite loops
let isSigningIn = false

/**
 * A robust API client that handles authentication and base path resolution.
 * @param endpoint The API endpoint to call, e.g., '/api/test' or '/api/admin/dashboard'.
 *                 The endpoint should include the '/api' prefix.
 * @param options Standard fetch options (method, body, etc.).
 */
async function apiClientFetch<T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const session = await getSession()

  const headers = new Headers(options.headers || {})

  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }

  // Handle session errors that occurred during a token refresh attempt.
  if (session?.error === 'RefreshAccessTokenError') {
    // Prevent infinite sign-in loops
    if (isSigningIn) {
      console.error('Already attempting to sign in. Please wait...')
      throw new Error('Authentication in progress. Please wait and try again.')
    }
    
    // Force sign-in to get a new, valid session.
    isSigningIn = true
    console.warn('Session refresh failed. Redirecting to sign-in...')
    // Use replace to avoid back button issues
    await signIn('keycloak', { callbackUrl: window.location.pathname, redirect: true })
    isSigningIn = false
    // Throw an error to stop the current, failing API call.
    throw new Error('Session expired. Please sign in again.')
  }

  // Construct the full URL: http://localhost:8001/app1/api/test
  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`

  const response = await fetch(fullUrl, { ...options, headers })

  // If the backend returns a 401, it means the token is invalid or expired.
  // This can happen if the backend is restarted or the user's session is revoked.
  // We trigger a re-login to fix the session.
  if (response.status === 401) {
    // Prevent infinite sign-in loops
    if (isSigningIn) {
      console.error('Already attempting to sign in. Please wait...')
      throw new Error('Authentication in progress. Please wait and try again.')
    }
    
    // Get fresh session to check if token refresh is needed
    const freshSession = await getSession()
    
    // If session has error or no token, trigger sign-in
    if (freshSession?.error || !freshSession?.accessToken) {
      isSigningIn = true
      console.warn('Session expired or invalid. Redirecting to sign-in...')
      await signIn('keycloak', { callbackUrl: window.location.pathname })
      isSigningIn = false
      throw new Error('Your session has expired. Redirecting to sign-in.')
    }
    
    // If session appears valid, it might be a backend issue or token was just refreshed
    // Try to retry once with the fresh session
    console.log('Retrying request with fresh session token...')
    const retryHeaders = new Headers(options.headers || {})
    if (freshSession?.accessToken) {
      retryHeaders.set('Authorization', `Bearer ${freshSession.accessToken}`)
    }
    
    const retryResponse = await fetch(`${API_URL}${BASE_PATH}${endpoint}`, { 
      ...options, 
      headers: retryHeaders 
    })
    
    // If retry also fails with 401, then sign in
    if (retryResponse.status === 401) {
      isSigningIn = true
      console.warn('Retry failed. Session truly invalid. Redirecting to sign-in...')
      await signIn('keycloak', { callbackUrl: window.location.pathname })
      isSigningIn = false
      throw new Error('Your session has expired. Redirecting to sign-in.')
    }
    
    // If retry succeeded, return that response
    if (retryResponse.ok) {
      if (retryResponse.status === 204) {
        return null as T
      }
      const contentType = retryResponse.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        return retryResponse.json() as Promise<T>
      }
      // Non-JSON successful response
      return null as T
    }
    
    // If retry failed with other error, handle normally
    let errorMessage = 'An API error occurred'
    try {
      const contentType = retryResponse.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const errorData = await retryResponse.json()
        errorMessage = errorData.detail || errorMessage
      } else {
        const text = await retryResponse.text()
        errorMessage = text || `Request failed with status ${retryResponse.status}`
      }
    } catch {
      errorMessage = `Request failed with status ${retryResponse.status}`
    }
    throw new Error(errorMessage)
  }

  if (!response.ok) {
    let errorMessage = 'An API error occurred'
    try {
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json()
        errorMessage = errorData.detail || errorMessage
      } else {
        const text = await response.text()
        errorMessage = text || `Request failed with status ${response.status}`
      }
    } catch {
      errorMessage = `Request failed with status ${response.status}`
    }
    throw new Error(errorMessage)
  }

  // Handle responses with no content
  if (response.status === 204) {
    return null as T
  }

  // Check content type before parsing as JSON
  const contentType = response.headers.get('content-type')
  if (contentType && contentType.includes('application/json')) {
    return response.json() as Promise<T>
  }
  
  // Non-JSON successful response
  return null as T
}

/**
 * API client with convenience methods for common HTTP operations.
 */
export const apiClient = {
  /**
   * Perform a GET request.
   */
  get: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'GET' })
  },

  /**
   * Perform a POST request.
   */
  post: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PUT request.
   */
  put: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PATCH request.
   */
  patch: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a DELETE request.
   */
  delete: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'DELETE' })
  },

  /**
   * POST a FormData payload (e.g., file uploads).
   * Does NOT set Content-Type — the browser handles multipart boundaries automatically.
   */
  postForm: <T = unknown>(endpoint: string, formData: FormData, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body: formData,
    })
  },

  /**
   * Download bulk call analysis as a single horizontal Excel file.
   */
  downloadBulkExcel: async (options: {
    startDate?: string
    endDate?: string
    limit?: number
    callIds?: number[]
  }): Promise<void> => {
    const session = await getSession()
    if (session?.error === 'RefreshAccessTokenError') {
      if (isSigningIn) {
        throw new Error('Authentication in progress. Please wait and try again.')
      }
      isSigningIn = true
      await signIn('keycloak', { callbackUrl: window.location.pathname })
      isSigningIn = false
      throw new Error('Session expired. Redirecting to sign-in.')
    }
    const fullUrl = `${API_URL}${BASE_PATH}/api/analysis/export/bulk-excel`
    const body: Record<string, unknown> = {}
    if (options.startDate) body.start_date = options.startDate
    if (options.endDate) body.end_date = options.endDate
    if (options.limit) body.limit = options.limit
    if (options.callIds?.length) body.call_ids = options.callIds

    const res = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.accessToken ? { Authorization: `Bearer ${session.accessToken}` } : {}),
      },
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      let errorMessage = 'Download failed'
      try {
        const contentType = res.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          const err = await res.json()
          errorMessage = err.detail || errorMessage
        } else {
          errorMessage = `Download failed with status ${res.status}`
        }
      } catch {
        errorMessage = `Download failed with status ${res.status}`
      }
      throw new Error(errorMessage)
    }
    const blob = await res.blob()
    const disp = res.headers.get('Content-Disposition')
    const match = disp?.match(/filename="?([^";\n]+)"?/)
    const filename = match ? match[1].trim() : `bulk-call-analysis.xlsx`
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  },

  /**
   * Download a CSV file from S3 storage via the backend proxy endpoint.
   * Uses auth session and triggers browser download. No presigned URL exposed to client.
   */
  downloadS3CsvFile: async (filePath: string, fileName: string): Promise<void> => {
    const session = await getSession()
    if (session?.error === 'RefreshAccessTokenError') {
      if (isSigningIn) {
        throw new Error('Authentication in progress. Please wait and try again.')
      }
      isSigningIn = true
      await signIn('keycloak', { callbackUrl: window.location.pathname })
      isSigningIn = false
      throw new Error('Session expired. Redirecting to sign-in.')
    }
    const params = new URLSearchParams({ file_path: filePath })
    const fullUrl = `${API_URL}${BASE_PATH}/api/analysis/s3/download-csv?${params}`
    const res = await fetch(fullUrl, {
      method: 'GET',
      headers: session?.accessToken ? { Authorization: `Bearer ${session.accessToken}` } : {},
    })
    if (!res.ok) {
      let errorMessage = 'Download failed'
      try {
        const contentType = res.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          const err = await res.json()
          errorMessage = err.detail || errorMessage
        } else {
          errorMessage = `Download failed with status ${res.status}`
        }
      } catch {
        errorMessage = `Download failed with status ${res.status}`
      }
      throw new Error(errorMessage)
    }
    const blob = await res.blob()
    const disp = res.headers.get('Content-Disposition')
    const match = disp?.match(/filename="?([^";\n]+)"?/)
    const resolvedFilename = match ? match[1].trim() : fileName
    const objectUrl = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = resolvedFilename
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(objectUrl)
  },

  /**
   * Download call analysis as Excel. Uses auth session and triggers browser download.
   */
  downloadCallAnalysisExcel: async (callId: string): Promise<void> => {
    const session = await getSession()
    if (session?.error === 'RefreshAccessTokenError') {
      if (isSigningIn) {
        throw new Error('Authentication in progress. Please wait and try again.')
      }
      isSigningIn = true
      await signIn('keycloak', { callbackUrl: window.location.pathname })
      isSigningIn = false
      throw new Error('Session expired. Redirecting to sign-in.')
    }
    const fullUrl = `${API_URL}${BASE_PATH}/api/analysis/${callId}/export/excel`
    const res = await fetch(fullUrl, {
      method: 'GET',
      headers: session?.accessToken ? { Authorization: `Bearer ${session.accessToken}` } : {},
    })
    if (!res.ok) {
      let errorMessage = 'Download failed'
      try {
        const contentType = res.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          const err = await res.json()
          errorMessage = err.detail || errorMessage
        } else {
          const text = await res.text()
          errorMessage = text || `Download failed with status ${res.status}`
        }
      } catch {
        errorMessage = `Download failed with status ${res.status}`
      }
      throw new Error(errorMessage)
    }
    const blob = await res.blob()
    const disp = res.headers.get('Content-Disposition')
    const match = disp?.match(/filename="?([^";\n]+)"?/)
    const filename = match ? match[1].trim() : `call-analysis-${callId}.xlsx`
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  },
}

// Default export for backward compatibility
export default apiClientFetch